import { Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { debounceTime, distinctUntilChanged, finalize, forkJoin, Subject, takeUntil } from 'rxjs';
import { ToastrService } from 'ngx-toastr';
import { TranslateService } from '@ngx-translate/core';
import { CoachTaskService } from '../../../core/services/coach-task.service';
import { EActorType, ICoachTask } from '@gray/domain/models/coach/coach-task.model';

const PAGE_SIZE = 10;
const MAX_LENGTH = 2000;

type TTaskFilter = 'all' | 'open' | 'completed';

/**
 * The tasks a coach assigned to a student. The student sees the same list and may tick items off,
 * so the row reports who completed one rather than assuming it was the coach.
 */
@Component({
  selector: 'coach-tasks-tab',
  templateUrl: './tasks-tab.component.html',
  styleUrl: './tasks-tab.component.scss',
})
export class TasksTabComponent implements OnInit, OnChanges, OnDestroy {
  @Input() studentId!: number;

  /** Reported upwards so the tab bar can show the same "3/5" counter. */
  @Output() progressChanged = new EventEmitter<string | null>();

  tasks: ICoachTask[] = [];
  page = 1;
  lastPage = 1;

  /** Totals come from the API's meta rather than the loaded page, so paging cannot skew them. */
  totalCount = 0;
  completedCount = 0;

  filter: TTaskFilter = 'all';
  searchControl = new FormControl<string>('');

  fg: FormGroup = this.formBuilder.group({
    content: [null, [Validators.required, Validators.maxLength(MAX_LENGTH)]],
  });

  editingTaskId?: number;
  editControl = new FormControl<string>('', [Validators.required, Validators.maxLength(MAX_LENGTH)]);

  isLoading = false;
  isSaving = false;
  isSubmitted = false;
  editIsSaving = false;

  actorTypes = EActorType;

  destroyed$ = new Subject();

  constructor(
    private formBuilder: FormBuilder,
    private taskService: CoachTaskService,
    private toastrService: ToastrService,
    private translate: TranslateService,
  ) {
  }

  ngOnInit(): void {
    this.searchControl.valueChanges
      .pipe(takeUntil(this.destroyed$), debounceTime(400), distinctUntilChanged())
      .subscribe(() => this.reload());
  }

  ngOnChanges(): void {
    if (this.studentId) {
      this.tasks = [];
      this.reload();
    }
  }

  progressLabel(): string | null {
    return this.totalCount ? `${this.completedCount}/${this.totalCount}` : null;
  }

  progressPercent(): number {
    return this.totalCount ? Math.round((this.completedCount / this.totalCount) * 100) : 0;
  }

  hasMore(): boolean {
    return this.page < this.lastPage;
  }

  setFilter(filter: TTaskFilter) {
    if (this.filter === filter) {
      return;
    }

    this.filter = filter;
    this.reload();
  }

  reload() {
    this.page = 1;
    this.getTasks();
    this.getCounts();
  }

  getTasks(append = false) {
    const parts = [`page=${this.page}`, `size=${PAGE_SIZE}`];
    if (this.filter !== 'all') {
      parts.push(`filter[isCompleted]=${this.filter === 'completed'}`);
    }

    const search = this.searchControl.value?.trim();
    if (search) {
      parts.push(`search=${encodeURIComponent(search)}`);
    }

    this.isLoading = true;
    this.taskService.getTasks(this.studentId, `?${parts.join('&')}`)
      .pipe(takeUntil(this.destroyed$), finalize(() => this.isLoading = false))
      .subscribe(r => {
        const result = (r.data?.result ?? []) as ICoachTask[];
        this.tasks = append ? [...this.tasks, ...result] : result;
        this.lastPage = r.data?.meta?.lastPage ?? 1;
      });
  }

  /** Two one-row reads: the totals must cover every task, not just the page on screen. */
  getCounts() {
    forkJoin({
      all: this.taskService.getTasks(this.studentId, '?page=1&size=1'),
      completed: this.taskService.getTasks(this.studentId, '?page=1&size=1&filter[isCompleted]=true'),
    })
      .pipe(takeUntil(this.destroyed$))
      .subscribe(r => {
        this.totalCount = r.all.data?.meta?.total ?? 0;
        this.completedCount = r.completed.data?.meta?.total ?? 0;
        this.progressChanged.emit(this.progressLabel());
      });
  }

  loadMore() {
    this.page += 1;
    this.getTasks(true);
  }

  onSubmit() {
    this.isSubmitted = true;
    if (this.fg.invalid) {
      return;
    }

    this.isSaving = true;
    this.taskService.createTask(this.studentId, this.fg.value.content)
      .pipe(takeUntil(this.destroyed$), finalize(() => this.isSaving = false))
      .subscribe(() => {
        this.toastrService.success(this.translate.instant('students.tasks.created'));
        this.isSubmitted = false;
        this.fg.reset();
        this.reload();
      });
  }

  /** Answers the click at once; the API only confirms it, and a failure puts the tick back. */
  toggleTask(task: ICoachTask) {
    const wasCompleted = task.isCompleted;
    task.isCompleted = !wasCompleted;
    this.completedCount += wasCompleted ? -1 : 1;
    this.progressChanged.emit(this.progressLabel());

    this.taskService.toggleTask(task.id, !wasCompleted)
      .pipe(takeUntil(this.destroyed$))
      .subscribe({
        next: () => {
          // A filtered list no longer holds a task that changed side.
          if (this.filter !== 'all') {
            this.tasks = this.tasks.filter(current => current.id !== task.id);
          }
        },
        error: () => {
          task.isCompleted = wasCompleted;
          this.completedCount += wasCompleted ? 1 : -1;
          this.progressChanged.emit(this.progressLabel());
        },
      });
  }

  startEdit(task: ICoachTask) {
    this.editingTaskId = task.id;
    this.editControl.setValue(task.content);
  }

  cancelEdit() {
    this.editingTaskId = undefined;
    this.editControl.reset();
  }

  saveEdit(task: ICoachTask) {
    if (this.editControl.invalid) {
      return;
    }

    this.editIsSaving = true;
    this.taskService.updateTask(task.id, this.editControl.value)
      .pipe(takeUntil(this.destroyed$), finalize(() => this.editIsSaving = false))
      .subscribe(() => {
        task.content = this.editControl.value;
        this.toastrService.success(this.translate.instant('students.tasks.updated'));
        this.cancelEdit();
      });
  }

  async confirmDelete(task: ICoachTask) {
    const Swal = (await import('sweetalert2')).default;

    const result = await Swal.fire({
      text: this.translate.instant('students.tasks.delete_confirm'),
      showCancelButton: true,
      confirmButtonText: this.translate.instant('buttons.yes'),
      cancelButtonText: this.translate.instant('buttons.cancel_l'),
      heightAuto: false,
    });

    if (result.isConfirmed) {
      this.deleteTask(task);
    }
  }

  deleteTask(task: ICoachTask) {
    this.taskService.deleteTask(task.id)
      .pipe(takeUntil(this.destroyed$))
      .subscribe(() => {
        this.toastrService.success(this.translate.instant('students.tasks.deleted'));
        this.tasks = this.tasks.filter(current => current.id !== task.id);
        this.totalCount = Math.max(0, this.totalCount - 1);
        if (task.isCompleted) {
          this.completedCount = Math.max(0, this.completedCount - 1);
        }
        this.progressChanged.emit(this.progressLabel());
      });
  }

  /** Only worth a badge when the task did not come from the coach themselves. */
  sourceLabelKey(task: ICoachTask): string | null {
    switch (task.createdBySource) {
      case EActorType.Admin:
        return 'students.tasks.source_admin';
      case EActorType.System:
        return 'students.tasks.source_system';
      case EActorType.Mentor:
        return 'students.tasks.source_mentor';
      case EActorType.Student:
        return 'students.tasks.source_student';
      default:
        return null;
    }
  }

  ngOnDestroy(): void {
    this.destroyed$.next(true);
    this.destroyed$.complete();
  }
}
